/**
 * Material codex + discovery log — docs/03 §7.
 *
 * The codex is an *emergent document*: it fills in with what this player
 * actually observed, in the order they observed it, which is a far better
 * teacher than a static wiki. Two consequences shape this file:
 *
 *   1. The reaction log is ordered by `discoveredAt`, never by id or alphabet.
 *      The list is a diary, and re-sorting it would destroy the only structure
 *      that matters.
 *   2. Undiscovered entries still occupy their row, redacted. Seeing the gaps is
 *      what makes filling them feel like progress — but the gap must not leak
 *      the answer, so it shows the slot, not the pair.
 *
 * Thumbnails are captured by the sim and handed over as an ImageBitmap (no
 * encode cost) or a dataURL (came out of a save file). This file only displays
 * them.
 */

import { btn, clear, el } from './style';
import {
  formatStatValue,
  standingTone,
  type CodexVM,
  type EnemyEntryVM,
  type FactionAxisVM,
  type FactionsVM,
  type FactionVM,
  type GameVM,
  type IndexedThumbVM,
  type MaterialVM,
  type ProgressionVM,
  type ReactionEntryVM,
  type ThumbVM,
  type TrackVM,
  type UICallbacks,
} from './viewModels';

type Section = 'materials' | 'reactions' | 'enemies' | 'tracks' | 'factions';

const SECTIONS: readonly Section[] = ['materials', 'reactions', 'enemies', 'tracks', 'factions'];

export class CodexPanel {
  readonly root: HTMLElement;

  private cb: UICallbacks;
  private codex: CodexVM | null = null;
  private prog: ProgressionVM | null = null;
  private fx: FactionsVM | null = null;
  private rev = -1;
  private progRev = -1;
  private fxRev = -1;

  private section: Section = 'materials';
  private q = '';
  private showUndiscovered = true;
  /** One pinned entry per section. The detail pane promised this and only
   *  honoured it for materials, so three quarters of the panel told the player
   *  to click something that did nothing. */
  private selectedMat: string | null = null;
  private selectedRx: string | null = null;
  private selectedEnemy: string | null = null;
  private selectedFaction: string | null = null;

  private dirty = true;
  private tabBtns: Record<Section, HTMLButtonElement>;
  private search: HTMLInputElement;
  private cbUndiscovered: HTMLInputElement;
  private listHost: HTMLElement;
  private detailHost: HTMLElement;
  private countText: HTMLElement;
  /** ImageBitmaps must be drawn; cache the canvases so scrolling is free. */
  private thumbCache = new Map<string, HTMLCanvasElement>();

  constructor(cb: UICallbacks) {
    this.cb = cb;
    this.root = el('div', 'panel');
    this.root.tabIndex = -1;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'Codex');

    const head = el('div', 'panel__head');
    head.append(el('div', 'panel__title', 'Codex'));

    const tabs = el('div', 'tabs');
    const mk = (s: Section, label: string): HTMLButtonElement => {
      const b = btn(label, 'section');
      b.dataset.section = s;
      tabs.appendChild(b);
      return b;
    };
    this.tabBtns = {
      materials: mk('materials', 'Materials'),
      reactions: mk('reactions', 'Reactions'),
      enemies: mk('enemies', 'Enemies'),
      tracks: mk('tracks', 'Progression'),
      // Reputation has to be legible from day one, before any vendor exists —
      // otherwise the first time a door is shut in the player's face is also the
      // first time they learn there was a door (#11).
      factions: mk('factions', 'Factions'),
    };
    head.append(tabs);

    this.countText = el('div', 'ui-mute ui-grow');
    head.append(this.countText);

    this.search = document.createElement('input');
    this.search.type = 'search';
    this.search.placeholder = 'search the codex…';
    this.search.setAttribute('aria-label', 'Search codex');
    head.append(this.search);

    const lab = el('label', 'ui-dim');
    this.cbUndiscovered = document.createElement('input');
    this.cbUndiscovered.type = 'checkbox';
    this.cbUndiscovered.checked = true;
    lab.append(this.cbUndiscovered, document.createTextNode('show gaps'));
    head.append(lab, btn('✕', 'close'));

    const body = el('div', 'panel__body');
    const centre = el('div', 'col ui-grow');
    this.listHost = el('div', 'scroll');
    centre.append(this.listHost);

    const right = el('div', 'col col--right');
    right.append(el('div', 'col__head', 'Entry'));
    this.detailHost = el('div', 'scroll detail');
    right.append(this.detailHost);
    body.append(centre, right);

    const foot = el('div', 'panel__foot');
    foot.append(
      el(
        'span',
        '',
        'Discoveries are recorded in the order you made them. Codex completion feeds the Codex meta track.',
      ),
    );

    this.root.append(head, body, foot);
    this.wire();
  }

  private wire(): void {
    this.root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const actEl = t.closest<HTMLElement>('[data-act]');
      const act = actEl?.dataset.act;
      if (act === 'close') {
        this.cb.onScreenChange?.('none');
        return;
      }
      if (act === 'section') {
        this.setSection(actEl!.dataset.section as Section);
        return;
      }
      const mat = t.closest<HTMLElement>('.mat');
      if (mat?.dataset.id) {
        this.selectedMat = mat.dataset.id;
        this.dirty = true;
        this.render();
        return;
      }
      const rx = t.closest<HTMLElement>('.rx');
      if (rx?.dataset.id) {
        this.selectedRx = rx.dataset.id;
        this.dirty = true;
        this.render();
        return;
      }
      const en = t.closest<HTMLElement>('.enemy');
      if (en?.dataset.id) {
        this.selectedEnemy = en.dataset.id;
        this.dirty = true;
        this.render();
        return;
      }
      const fx = t.closest<HTMLElement>('.fx__pole');
      if (fx?.dataset.id) {
        this.selectedFaction = fx.dataset.id;
        this.dirty = true;
        this.render();
      }
    });

    this.search.addEventListener('input', () => {
      this.q = this.search.value.trim().toLowerCase();
      this.dirty = true;
      this.render();
    });
    this.cbUndiscovered.addEventListener('change', () => {
      this.showUndiscovered = this.cbUndiscovered.checked;
      this.dirty = true;
      this.render();
    });
  }

  private setSection(s: Section): void {
    if (this.section === s) return;
    this.section = s;
    this.dirty = true;
    this.render();
  }

  /** Route a codex link from the loadout panel (docs/04 §7, codex integration). */
  openRef(kind: 'material' | 'reaction' | 'enemy', id: string): void {
    if (kind === 'material') {
      this.section = 'materials';
      this.selectedMat = id;
    } else if (kind === 'reaction') {
      this.section = 'reactions';
      this.selectedRx = id;
    } else {
      this.section = 'enemies';
      this.selectedEnemy = id;
    }
    this.q = '';
    this.search.value = '';
    this.dirty = true;
    this.render();
    if (kind !== 'material') {
      // Bring the entry into view once the list exists.
      const node = this.listHost.querySelector<HTMLElement>(`[data-id="${cssEscape(id)}"]`);
      node?.scrollIntoView({ block: 'center' });
    }
  }

  onShow(): void {
    this.dirty = true;
    this.render();
    this.root.focus();
  }

  update(vm: GameVM): void {
    this.codex = vm.codex;
    this.prog = vm.progression;
    this.fx = vm.factions;
    if (
      vm.codex.revision !== this.rev ||
      vm.progression.revision !== this.progRev ||
      vm.factions.revision !== this.fxRev
    ) {
      this.rev = vm.codex.revision;
      this.progRev = vm.progression.revision;
      this.fxRev = vm.factions.revision;
      this.dirty = true;
    }
    this.render();
  }

  private render(): void {
    if (!this.dirty || !this.codex) return;
    this.dirty = false;

    for (const s of SECTIONS) {
      this.tabBtns[s].dataset.on = this.section === s ? '1' : '0';
    }

    clear(this.listHost);
    switch (this.section) {
      case 'materials':
        this.renderMaterials(this.codex);
        break;
      case 'reactions':
        this.renderReactions(this.codex);
        break;
      case 'enemies':
        this.renderEnemies(this.codex);
        break;
      case 'tracks':
        this.renderTracks();
        break;
      case 'factions':
        this.renderFactions();
        break;
    }
    this.renderDetail();
  }

  // ------------------------------------------------------------- materials

  private renderMaterials(cx: CodexVM): void {
    const grid = el('div', 'cx');
    let found = 0;
    for (const m of cx.materials) {
      if (m.discovered) found++;
      if (!m.discovered && !this.showUndiscovered) continue;
      if (this.q && !matBlob(m).includes(this.q)) continue;

      const card = el('div', 'mat');
      card.dataset.id = m.id;
      card.dataset.found = m.discovered ? '1' : '0';
      if (this.selectedMat === m.id) card.dataset.on = '1';
      const sw = el('div', 'mat__sw');
      sw.style.background = m.discovered ? m.color : '#1a1c24';
      card.append(sw, el('div', 'mat__n', m.discovered ? m.name : '???'));
      card.title = m.discovered ? m.name + ' — ' + m.category : 'undiscovered';
      grid.append(card);
    }
    this.listHost.append(grid);
    this.countText.textContent = found + ' / ' + cx.totals.materials + ' materials';
  }

  // ------------------------------------------------------------- reactions

  /** Discovery order. Never re-sorted — the ordering is the document. */
  private renderReactions(cx: CodexVM): void {
    const list = [...cx.reactions].sort((a, b) => a.discoveredAt - b.discoveredAt);
    const frag = document.createDocumentFragment();
    let found = 0;
    let n = 0;
    for (const r of list) {
      if (r.discovered) found++;
      if (!r.discovered && !this.showUndiscovered) continue;
      if (this.q && r.discovered && !rxBlob(r).includes(this.q)) continue;
      if (this.q && !r.discovered) continue;
      frag.append(this.reactionRow(r, ++n));
    }
    this.listHost.append(frag);
    this.countText.textContent = found + ' / ' + cx.totals.reactions + ' reactions';
  }

  private reactionRow(r: ReactionEntryVM, ordinal: number): HTMLElement {
    const row = el('div', 'rx');
    row.dataset.id = r.id;
    row.dataset.found = r.discovered ? '1' : '0';
    if (this.selectedRx === r.id) row.dataset.on = '1';

    row.append(el('div', 'rx__ord', '#' + ordinal));
    row.append(this.thumbFor(r));

    const b = el('div', 'rx__b ui-grow');
    if (r.discovered) {
      const eq = el('div', 'rx__eq');
      eq.append(el('b', '', r.a));
      eq.append(document.createTextNode(' + '));
      eq.append(el('b', '', r.b));
      eq.append(document.createTextNode(' → '));
      eq.append(el('i', '', r.outcome));
      b.append(eq);
      b.append(el('div', 'rx__meta', 'when ' + r.condition));
      b.append(
        el(
          'div',
          'rx__meta',
          (r.event ? r.event + ' · ' : '') + 'observed ' + r.seenCount + '×',
        ),
      );
    } else {
      b.append(el('div', 'rx__eq ui-mute', '??? + ??? → ???'));
      b.append(el('div', 'rx__meta', 'not yet observed'));
    }
    // The Reaction Hints passive, when it has something honest to say. It is
    // shown on discovered rows too — at the top level it predicts the chain a
    // known product feeds, which is a fact about something you have already
    // made rather than a nudge toward something you have not.
    if (r.hint) b.append(el('div', 'rx__meta ui-good', r.hint));
    row.append(b);
    return row;
  }

  /**
   * Every captured form needs a canvas; they are cached by entry id so a scroll
   * or a re-render never re-decodes.
   *
   * A reaction is only photographed the first time it fires and the store is
   * capped, so the no-thumb path stays the common one: it falls back to the
   * reactants' and product's real colours rather than an empty box, which is
   * the difference between "not photographed yet" and "this image is broken".
   */
  private thumbFor(r: ReactionEntryVM): HTMLElement {
    if (!r.discovered) return el('div', 'rx__thumb');
    if (!r.thumb) return swatchThumb(r.swatch);
    const cached = this.thumbCache.get(r.id);
    if (cached) return cached;

    const c = document.createElement('canvas');
    c.className = 'rx__thumb';
    c.width = 64;
    c.height = 64;
    drawThumb(c, r.thumb);
    this.thumbCache.set(r.id, c);
    return c;
  }

  private reactionDetail(r: ReactionEntryVM): DocumentFragment {
    const f = document.createDocumentFragment();
    f.append(el('div', 'detail__title', r.a + ' + ' + r.b));
    f.append(el('div', 'detail__prose', '→ ' + r.outcome));
    f.append(el('div', 'detail__line', 'condition: ' + r.condition));
    if (r.event) f.append(el('div', 'detail__line', 'event: ' + r.event));
    if (r.hint) f.append(el('div', 'detail__prose ui-good', r.hint));
    f.append(el('div', 'detail__line', 'discovery #' + r.discoveredAt));
    f.append(el('div', 'detail__line', 'observed ' + r.seenCount + ' times'));
    if (r.thumb) {
      const c = document.createElement('canvas');
      c.className = 'rx__thumb';
      c.width = 64;
      c.height = 64;
      c.style.width = '128px';
      c.style.height = '128px';
      drawThumb(c, r.thumb);
      f.append(c);
    } else {
      const s = swatchThumb(r.swatch);
      s.style.width = '128px';
      s.style.height = '128px';
      f.append(s);
    }

    // Which materials this reaction touches, as links — the codex is one
    // document, so a reaction has to be walkable back into its reactants.
    const box = el('div', 'syn');
    box.append(el('div', 'syn__lead', 'reactants'));
    box.append(el('div', 'syn__item', r.a + '  +  ' + r.b));
    f.append(box);
    return f;
  }

  private enemyDetail(e: EnemyEntryVM): DocumentFragment {
    const f = document.createDocumentFragment();
    f.append(el('div', 'detail__title', e.discovered ? e.name : 'Unidentified'));
    f.append(el('div', 'detail__line', e.behaviour));
    if (!e.discovered) {
      f.append(
        el('div', 'detail__prose', 'You have neither killed one of these nor been killed by one.'),
      );
      return f;
    }
    f.append(el('div', 'detail__line', e.killCount + ' killed · ' + e.deathsTo + ' deaths to it'));

    // Composition and its consequences are one disclosure, gated on the
    // Composition Autopsy passive. Three empty sections read as a codex that
    // failed to load; one sentence naming the unlock reads as a thing to earn.
    if (e.redacted) {
      f.append(el('div', 'detail__prose ui-warn', e.redactedHint));
      return f;
    }

    const comp = el('div', 'enemy__comp');
    for (const c of e.composition) {
      const seg = el('i');
      seg.style.width = Math.round(c.fraction * 100) + '%';
      seg.style.background = c.color;
      seg.title = c.mat + ' ' + fmtPct(c.fraction);
      comp.append(seg);
    }
    f.append(comp);
    f.append(
      el('div', 'rx__meta', e.composition.map((c) => c.mat + ' ' + fmtPct(c.fraction)).join(' · ')),
    );

    // The reason, not just the multiplier: a codex that lists numbers is a
    // cheat sheet, one that states the composition behind them is a teacher
    // (docs/03 §6).
    const box = el('div', 'syn');
    box.append(el('div', 'syn__lead', 'what works'));
    if (e.weaknesses.length === 0) box.append(el('div', 'syn__none', 'nothing it is soft to'));
    for (const w of e.weaknesses) {
      const d = el('div', 'syn__item');
      d.append(el('b', '', w.label));
      d.append(document.createTextNode(' — ' + w.reason));
      box.append(d);
    }
    box.append(el('div', 'syn__lead', 'what does not'));
    if (e.resistances.length === 0) box.append(el('div', 'syn__none', 'nothing it shrugs off'));
    for (const w of e.resistances) {
      const d = el('div', 'syn__item');
      d.append(el('b', '', w.label));
      d.append(document.createTextNode(' — ' + w.reason));
      box.append(d);
    }
    f.append(box);
    return f;
  }

  // ------------------------------------------------------------- enemies

  private renderEnemies(cx: CodexVM): void {
    const frag = document.createDocumentFragment();
    let found = 0;
    for (const e of cx.enemies) {
      if (e.discovered) found++;
      if (!e.discovered && !this.showUndiscovered) continue;
      if (this.q && !enemyBlob(e).includes(this.q)) continue;
      frag.append(this.enemyRow(e));
    }
    this.listHost.append(frag);
    this.countText.textContent = found + ' / ' + cx.totals.enemies + ' enemies';
  }

  private enemyRow(e: EnemyEntryVM): HTMLElement {
    const row = el('div', 'enemy');
    row.dataset.id = e.id;
    row.dataset.found = e.discovered ? '1' : '0';
    if (this.selectedEnemy === e.id) row.dataset.on = '1';

    const head = el('div', 'ui-row');
    head.append(el('span', '', e.discovered ? e.name : '???'));
    head.append(el('span', 'ui-mute', e.behaviour));
    head.append(el('span', 'ui-grow'));
    head.append(el('span', 'ui-mute', e.killCount + ' killed'));
    if (e.deathsTo > 0) {
      // docs/05 §4 — deaths feed Lineage, so they are progress, not shame.
      head.append(el('span', 'ui-warn', e.deathsTo + ' deaths → Lineage'));
    }
    row.append(head);

    // Composition drives everything (docs/03 §6), so it is shown as matter —
    // once Composition Autopsy is held. The list row says so in one short line;
    // the detail pane carries the full sentence.
    if (e.redacted) {
      row.append(el('div', 'rx__meta ui-warn', 'composition sealed'));
      return row;
    }

    const comp = el('div', 'enemy__comp');
    for (const c of e.composition) {
      const seg = el('i');
      seg.style.width = Math.round(c.fraction * 100) + '%';
      seg.style.background = c.color;
      seg.title = c.mat + ' ' + Math.round(c.fraction * 100) + '%';
      comp.append(seg);
    }
    row.append(comp);
    row.append(
      el('div', 'rx__meta', e.composition.map((c) => c.mat + ' ' + fmtPct(c.fraction)).join(' · ')),
    );

    if (e.discovered) {
      // Weaknesses are derived from composition — always state the reason, or
      // the codex is a cheat sheet rather than a teacher.
      for (const w of e.weaknesses) {
        const d = el('div', 'enemy__w');
        d.append(el('b', '', w.label));
        d.append(document.createTextNode(' — ' + w.reason));
        row.append(d);
      }
      for (const w of e.resistances) {
        const d = el('div', 'enemy__w res');
        d.append(el('b', '', w.label));
        d.append(document.createTextNode(' — ' + w.reason));
        row.append(d);
      }
    }
    return row;
  }

  // ------------------------------------------------------------- tracks

  /** docs/05 §4 — the five meta tracks, each fed by a different behaviour. */
  private renderTracks(): void {
    const frag = document.createDocumentFragment();
    for (const t of this.prog?.tracks ?? []) frag.append(trackRow(t));
    this.listHost.append(frag);
    this.countText.textContent = (this.prog?.tracks.length ?? 0) + ' progression tracks';
  }

  // ------------------------------------------------------------- factions

  /**
   * Three bars, not six columns — docs/05 §5, #11.
   *
   * The six factions sit in three opposed pairs sharing one signed axis each, so
   * reputation is zero-sum *by construction*: rep(a) is always exactly −rep(b).
   * Six independent gauges would render that fact as a coincidence. One bar with
   * a needle between two named poles renders it as the mechanism, which is the
   * only reason the system is interesting — the player has to be able to watch
   * pleasing one side cost them the other.
   */
  private renderFactions(): void {
    const f = this.fx;
    const frag = document.createDocumentFragment();
    if (!f || f.axes.length === 0) {
      this.listHost.append(el('div', 'detail__line', '  no standing to report'));
      this.countText.textContent = '';
      return;
    }

    if (f.untouched) {
      // Not an error state. A profile that has done nothing has standing with
      // everyone and owes nothing to anyone, and saying that is more use than
      // six zeroes the player has to interpret.
      frag.append(
        el(
          'div',
          'fx__none',
          'Nobody has an opinion of you yet. Burn something, mend something, ' +
            'or spill something, and two of these needles will move at once.',
        ),
      );
    }

    let open = 0;
    let hostile = 0;
    for (const a of f.axes) {
      if (this.q && !axisBlob(a).includes(this.q)) continue;
      frag.append(this.axisRow(a));
      for (const p of [a.pos, a.neg]) {
        if (p.shopOpen) open++;
        if (p.hostile) hostile++;
      }
    }

    if (f.deeds.length > 0) {
      const box = el('div', 'syn');
      // Sorted by total swing in the adapter: "why do they hate me" is answered
      // by the deed that moved the most, not the one that happened last.
      box.append(el('div', 'syn__lead', 'what you have been seen doing'));
      for (const d of f.deeds) {
        const swing = d.count * d.weight;
        const line = el('div', 'syn__item');
        line.append(el('b', '', d.name));
        line.append(document.createTextNode(' ×' + d.count + ' '));
        // Signed, because half of these are kills and carry negative weight —
        // "Emberwright slain ×12 → Emberwrights −12" is the sentence that
        // answers why their door is shut, and "+−12" is not a number.
        line.append(
          el('i', '', '→ ' + d.factionName + ' ' + (swing >= 0 ? '+' : '−') + Math.abs(swing)),
        );
        box.append(line);
      }
      frag.append(box);
    }

    this.listHost.append(frag);
    this.countText.textContent =
      open + ' of 6 will trade' + (hostile > 0 ? ' · ' + hostile + ' hunting you' : '');
  }

  private axisRow(a: FactionAxisVM): HTMLElement {
    const row = el('div', 'fx');
    row.dataset.id = a.id;

    const head = el('div', 'fx__head');
    head.append(this.poleName(a.pos, 'pos'));
    head.append(el('span', 'fx__axis ui-caps', a.id));
    head.append(this.poleName(a.neg, 'neg'));
    row.append(head);

    // The needle. `left` is a percentage of the bar rather than a transform
    // because the bar is laid out once per open, not per frame — this is a
    // panel, not the HUD, and legibility beats the compositor here.
    const bar = el('div', 'fx__bar');
    bar.append(el('i', 'fx__centre'));
    const needle = el('i', 'fx__needle');
    needle.style.left = (a.marker01 * 100).toFixed(1) + '%';
    needle.dataset.side = a.marker01 > 0.5 ? 'pos' : a.marker01 < 0.5 ? 'neg' : 'centre';
    bar.append(needle);
    row.append(bar);

    const foot = el('div', 'fx__foot');
    foot.append(this.poleState(a.pos));
    foot.append(el('span', 'ui-grow'));
    foot.append(this.poleState(a.neg));
    row.append(foot);
    return row;
  }

  private poleName(p: FactionVM, side: 'pos' | 'neg'): HTMLElement {
    const e = el('span', 'fx__pole');
    e.dataset.side = side;
    e.dataset.id = p.id;
    e.dataset.on = this.selectedFaction === p.id ? '1' : '0';
    e.textContent = p.name;
    return e;
  }

  /** Standing as a word first, tone second — colour alone is not a readout. */
  private poleState(p: FactionVM): HTMLElement {
    const e = el('span', 'fx__state');
    e.dataset.tone = standingTone(p.standing);
    const rep = p.reputation > 0 ? '+' + p.reputation : String(p.reputation);
    e.textContent = p.standing + ' ' + rep;
    if (!p.shopOpen) e.append(el('b', '', ' · closed'));
    if (p.hostile) e.append(el('b', '', ' · hunting ' + Math.round(p.hostileRate * 100) + '%'));
    return e;
  }

  private factionDetail(p: FactionVM): DocumentFragment {
    const f = document.createDocumentFragment();
    f.append(el('div', 'detail__title', p.name));
    const rep = p.reputation > 0 ? '+' + p.reputation : String(p.reputation);
    const line = el('div', 'detail__line');
    line.append(el('span', 'ui-mute', 'standing '));
    const st = el('span', 'fx__state', p.standing + ' ' + rep);
    st.dataset.tone = standingTone(p.standing);
    line.append(st);
    f.append(line);

    f.append(el('div', 'detail__prose', 'They want ' + p.wants + '.'));

    const box = el('div', 'syn');
    box.append(el('div', 'syn__lead', p.shopOpen ? 'what they will sell you' : 'what they would sell you'));
    for (const g of p.gives) box.append(el('div', 'syn__item', g));
    if (!p.shopOpen) {
      // The closed door names its own cause, the same contract crafting keeps.
      box.append(
        el('div', 'syn__warn', 'They will not trade — serving their opposite is what shut this.'),
      );
    } else {
      box.append(el('div', 'syn__none', 'stock tier ' + p.offerTier + ' of 3 — standing widens it'));
    }
    if (p.hostile) {
      box.append(
        el(
          'div',
          'syn__warn',
          Math.round(p.hostileRate * 100) + '% of what you meet of theirs now spawns hostile.',
        ),
      );
    }
    f.append(box);
    return f;
  }

  // ------------------------------------------------------------- detail

  private renderDetail(): void {
    if (this.section === 'factions') {
      clear(this.detailHost);
      const all = (this.fx?.axes ?? []).flatMap((a) => [a.pos, a.neg]);
      const p = all.find((x) => x.id === this.selectedFaction);
      if (!p) {
        this.detailHost.append(el('div', 'detail__line', 'Click a name to see what they want.'));
        return;
      }
      this.detailHost.append(this.factionDetail(p));
      return;
    }
    if (this.section === 'materials') {
      const m = this.codex?.materials.find((x) => x.id === this.selectedMat);
      clear(this.detailHost);
      if (!m) {
        this.detailHost.append(el('div', 'detail__line', 'Pick a material.'));
        return;
      }
      this.detailHost.append(this.materialDetail(m));
    } else if (this.section === 'reactions') {
      clear(this.detailHost);
      const r = this.codex?.reactions.find((x) => x.id === this.selectedRx);
      if (!r || !r.discovered) {
        this.detailHost.append(el('div', 'detail__line', 'Click a reaction to pin it here.'));
        return;
      }
      this.detailHost.append(this.reactionDetail(r));
    } else if (this.section === 'enemies') {
      clear(this.detailHost);
      const e = this.codex?.enemies.find((x) => x.id === this.selectedEnemy);
      if (!e) {
        this.detailHost.append(el('div', 'detail__line', 'Click an enemy to pin it here.'));
        return;
      }
      this.detailHost.append(this.enemyDetail(e));
    } else if (this.section === 'tracks') {
      clear(this.detailHost);
      this.detailHost.append(
        el(
          'div',
          'detail__prose',
          'Five tracks so no single playstyle starves. Ash comes from any run; Codex from ' +
            'discovery; Lineage from deaths — you unlock what killed you; Salvage from reaching ' +
            'new biomes; Attunement from bosses.',
        ),
      );
    }
  }

  private materialDetail(m: MaterialVM): DocumentFragment {
    const f = document.createDocumentFragment();
    const title = el('div', 'detail__title', m.discovered ? m.name : 'Undiscovered');
    f.append(title);
    if (!m.discovered) {
      f.append(el('div', 'detail__prose', 'You have not encountered this material yet.'));
      return f;
    }
    const sw = el('div', 'mat__sw');
    sw.style.background = m.color;
    sw.style.width = '48px';
    sw.style.height = '48px';
    f.append(sw);
    f.append(el('div', 'detail__line', 'category: ' + m.category));

    const tagRow = el('div', 'ui-row');
    for (const t of m.tags) tagRow.append(el('span', 'chip', t));
    f.append(tagRow);

    for (const p of m.props) {
      const r = el('div', 'detail__line');
      r.append(el('span', 'ui-mute', p.label + ' '));
      r.append(el('span', '', formatStatValue(p.value, p.format, p.unit)));
      f.append(r);
    }
    if (m.note) f.append(el('div', 'detail__prose', m.note));

    const box = el('div', 'syn');
    const known = m.knownReactionIds
      .map((id) => this.codex?.reactions.find((r) => r.id === id))
      .filter((r): r is ReactionEntryVM => !!r && r.discovered);
    box.append(el('div', 'syn__lead', known.length + ' known reactions'));
    if (known.length === 0)
      box.append(el('div', 'syn__none', 'nothing observed yet — go put it next to something'));
    for (const r of known) {
      box.append(el('div', 'syn__item', r.a + ' + ' + r.b + ' → ' + r.outcome));
    }
    f.append(box);
    return f;
  }

  handleKey(e: KeyboardEvent): boolean {
    const typing = document.activeElement instanceof HTMLInputElement;
    if (e.key === '/' && !typing) {
      this.search.focus();
      this.search.select();
      return true;
    }
    if (typing) return false;
    const order: Section[] = ['materials', 'reactions', 'enemies', 'tracks'];
    if (e.key >= '1' && e.key <= '4') {
      this.setSection(order[Number(e.key) - 1]);
      return true;
    }
    return false;
  }

  destroy(): void {
    this.thumbCache.clear();
    this.root.remove();
  }
}

// ------------------------------------------------------------------ helpers

export function trackRow(t: TrackVM): HTMLElement {
  const row = el('div', 'track');
  const h = el('div', 'track__h');
  h.append(el('span', 'track__n', t.glyph + ' ' + t.name));
  h.append(el('span', 'track__fed', 'fed by ' + t.fedBy));
  h.append(el('span', 'ui-grow'));
  h.append(el('span', 'ui-dim', 'lv ' + t.level + ' · ' + t.value + ' / ' + t.next));
  row.append(h);

  const b = el('div', 'bar');
  const i = el('i');
  i.style.setProperty('--c', 'var(--ui-accent)');
  i.style.setProperty('--f', String(t.next > 0 ? Math.min(1, t.value / t.next) : 1));
  b.append(i);
  row.append(b);

  const un = el('div', 'track__unlocks');
  for (const u of t.unlocks) {
    const chip = el('span', 'unlock', u.name + ' (' + u.at + ')');
    chip.dataset.on = u.unlocked ? '1' : '0';
    chip.title = u.desc;
    un.append(chip);
  }
  row.append(un);
  return row;
}

/**
 * The fallback for an uncaptured reaction: reactant, reactant, product, as
 * three bands of the materials' own colours. A side resolved through a tag has
 * no single colour and is left as the empty background rather than guessed.
 */
function swatchThumb(sw: ReactionEntryVM['swatch']): HTMLElement {
  const box = el('div', 'rx__thumb rx__swatch');
  if (!sw) return box;
  for (const c of [sw.a, sw.b, sw.out]) {
    const band = el('i');
    if (c) band.style.background = c;
    else band.dataset.any = '1';
    box.append(band);
  }
  return box;
}

/**
 * The indexed form is the one the capture path actually produces, so it is
 * drawn at its native cell size into a scratch canvas and then blown up with
 * smoothing off. Writing ImageData straight into the 64px canvas would need the
 * caller to have already scaled it, and the whole point of an indexed bitmap is
 * that nobody has to.
 */
function drawIndexed(c: HTMLCanvasElement, ctx: CanvasRenderingContext2D, t: IndexedThumbVM): void {
  const src = document.createElement('canvas');
  src.width = t.w;
  src.height = t.h;
  const sctx = src.getContext('2d');
  if (!sctx) return;
  const img = sctx.createImageData(t.w, t.h);
  const px = img.data;
  for (let i = 0; i < t.data.length; i++) {
    const rgb = t.palette[t.data[i]] ?? -1;
    const o = i * 4;
    // A negative palette entry is VOID — empty space, drawn as nothing rather
    // than as black, or a thumbnail of a cave reads as a solid block.
    if (rgb < 0) continue;
    px[o] = (rgb >> 16) & 0xff;
    px[o + 1] = (rgb >> 8) & 0xff;
    px[o + 2] = rgb & 0xff;
    px[o + 3] = 255;
  }
  sctx.putImageData(img, 0, 0);
  ctx.drawImage(src, 0, 0, c.width, c.height);
}

function drawThumb(c: HTMLCanvasElement, thumb: ThumbVM): void {
  const ctx = c.getContext('2d');
  if (!ctx) return;
  ctx.imageSmoothingEnabled = false;
  if (typeof thumb === 'string') {
    const img = new Image();
    img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
    img.src = thumb;
  } else if ('data' in thumb) {
    drawIndexed(c, ctx, thumb);
  } else {
    ctx.drawImage(thumb, 0, 0, c.width, c.height);
  }
}

function fmtPct(f: number): string {
  return Math.round(f * 100) + '%';
}

function matBlob(m: MaterialVM): string {
  return (m.name + ' ' + m.category + ' ' + m.tags.join(' ') + ' ' + (m.note ?? '')).toLowerCase();
}

function rxBlob(r: ReactionEntryVM): string {
  return (r.a + ' ' + r.b + ' ' + r.condition + ' ' + r.outcome + ' ' + (r.event ?? '')).toLowerCase();
}

/** Both poles and the axis, so a search for either name keeps the pair whole. */
function axisBlob(a: FactionAxisVM): string {
  return (
    a.id + ' ' + a.pos.name + ' ' + a.neg.name + ' ' +
    a.pos.standing + ' ' + a.neg.standing + ' ' + a.pos.wants + ' ' + a.neg.wants
  ).toLowerCase();
}

function enemyBlob(e: EnemyEntryVM): string {
  return (
    e.name +
    ' ' +
    e.behaviour +
    ' ' +
    e.composition.map((c) => c.mat).join(' ') +
    ' ' +
    e.weaknesses.map((w) => w.label + ' ' + w.reason).join(' ')
  ).toLowerCase();
}

/** Minimal attribute-selector escape; ids are content-authored, not arbitrary. */
function cssEscape(s: string): string {
  return s.replace(/["\\]/g, '\\$&');
}
